import { Droppable, getDroppablePayload } from "@msuite/ui/dnd";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import type { Tables } from "@msuite/utils/types";
import { mutators } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import { useState } from "react";
import { syncHandler } from "@/api/sync-handler";
import { useCitadel } from "@/hooks/use-citadel";
import type { UserAssignmentType } from "../use-crew-handler";
import { UserResources } from "./user-resources";
import { VehicleResources } from "./vehicle-resources";

export function Resources() {
	/** Hooks */
	const { t } = useTranslation();
	const z = useZero();
	const { citadel } = useCitadel();
	const [resourceType, setResourceType] = useState<"vehicles" | "users">("users");

	/** Computed */
	// The board is everybody's to read; the fleet is not. This panel lists every lorry the
	// organisation owns, from the same route the vehicles page reads, so without the rule the switch
	// offers people only. The lorries already assigned to a crew stay on the board itself — that is
	// the board's own record and it arrives through the sync path, not through this route.
	const canReadVehicles = citadel ? citadel.can("read", "Vehicle") : true;
	// The rules arrive after the first render, so the lorry segment can disappear under a reader who
	// is standing on it. The panel then shows the segment that is still there rather than nothing at
	// all, and the choice is kept in case the rule comes back.
	const segment = canReadVehicles ? resourceType : "users";

	/** Render */
	return (
		<div
			className="grow-0 min-h-0 overflow-hidden h-full w-[500px] px-4 border-l-[0.5px] shadow-lg border-color pb-1 grid gap-y-4"
			style={{ gridTemplateRows: "max-content 1fr" }}
		>
			<SegmentedControl
				value={segment}
				onChange={setResourceType}
				items={[
					{
						label: t("users.users"),
						value: "users",
						cyId: "resources-users",
					},
					...(canReadVehicles
						? [
								{
									label: t("vehicles.vehicles"),
									value: "vehicles" as const,
									cyId: "resources-vehicles",
								},
							]
						: []),
				]}
			/>
			<Droppable
				className="grow-0 w-full h-full min-h-0 overflow-hidden"
				accept={[
					"user-assignment",
					"vehicle-assignment",
					"vehicle-assignment-plan",
					"user-vehicle-assignment",
				]}
				handleOnDrop={(event) => {
					const payload = getDroppablePayload(event);
					if (payload.type === "user-assignment") {
						const item = payload.item as UserAssignmentType;
						syncHandler(z.mutate(mutators.userAssignments.delete({ userAssignmentId: item.id })));
					} else if (payload.type === "vehicle-assignment") {
						const item = payload.item as Tables<"vehicleAssignments">;
						syncHandler(
							z.mutate(mutators.vehicleAssignments.delete({ vehicleAssignmentId: item.id })),
						);
					} else if (payload.type === "vehicle-assignment-plan") {
						const item = payload.item as Tables<"vehicleAssignmentsPlan">;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.delete({ vehicleAssignmentPlanId: item.id }),
							),
						);
					} else if (payload.type === "user-vehicle-assignment") {
						const item = payload.item as Tables<"vehicleAssignmentsPlan">;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.removeUser({ vehicleAssignmentPlanId: item.id }),
							),
						);
					}
				}}
			>
				{segment === "users" && <UserResources />}
				{segment === "vehicles" && <VehicleResources />}
			</Droppable>
		</div>
	);
}
